import React, { useEffect, useMemo, useState } from 'react';
import { request } from '../../utils/remote';
import PageTitle from '../../components/PageTitle';

const RagList: React.FC = () => {
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chaptersByBook, setChaptersByBook] = useState<Record<string, any[]>>({});
  const [chunksByChapter, setChunksByChapter] = useState<Record<string, any[]>>({});
  const [activeBook, setActiveBook] = useState<any | null>(null);
  const [modalChunk, setModalChunk] = useState<string | null>(null);
  const [openChapters, setOpenChapters] = useState<Record<string, boolean>>({});
  const [vectorModalOpen, setVectorModalOpen] = useState(false);
  const [vectorQuery, setVectorQuery] = useState('');
  const [vectorResults, setVectorResults] = useState<any[] | null>(null);
  const [vectorLoading, setVectorLoading] = useState(false);

  const runVectorSearch = async () => {
    if (!activeBook) return;
    setVectorLoading(true);
    setVectorResults(null);
      try {
      const token = localStorage.getItem('token');
      // 1) call embed proxy to get embedding for query
      const embData = await request('/api/proxy/embed/', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: vectorQuery })
      });
      const embedding = embData.embedding;

      // 2) call vector search
      const vsData = await request(`/api/textbook/${activeBook.id}/vector-search/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ embedding, top_k: 10 })
      });
  // sort results by score ascending (lower distance = better match)
  // Sort descending by score (higher score = better). Fall back to -Infinity when missing so defined scores come first.
  const results = (vsData.results || []).slice().sort((a: any, b: any) => (b.score ?? Number.NEGATIVE_INFINITY) - (a.score ?? Number.NEGATIVE_INFINITY));
  setVectorResults(results);
  // keep modal open so results appear in it
  setVectorModalOpen(true);
    } catch (err: any) {
      setVectorResults([]);
      console.error(err);
      alert(err?.message || 'Vector search failed');
    } finally {
      setVectorLoading(false);
    }
  };

  // UI controls
  const [query, setQuery] = useState('');
  // removed unused indexed filter and sort controls in favor of grade/subject/time
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('any');
  const [selectedSort, setSelectedSort] = useState<string>('newest'); // Added state for sorting
  const [groupBy, setGroupBy] = useState<'none' | 'subject' | 'grade'>('none');

  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      try {
        const data = await request('/api/textbook/list/', { headers: { 'Authorization': `Bearer ${token}` } });
        setBooks(data.books || []);
      } catch (err: any) {
        setError(err?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchBooks();
  }, []);

  const filtered = useMemo(() => {
    let out = books.slice();
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((b) => (b.title || '').toLowerCase().includes(q) || (b.author || '').toLowerCase().includes(q));
    }
    // preserve index filter via search query (e.g. 'indexed:true') if needed (not implemented)
    if (selectedGrade) out = out.filter((b) => String(b.grade || '') === String(selectedGrade));
    if (selectedSubject) out = out.filter((b) => String(b.subject || '') === String(selectedSubject));
    if (selectedTime && selectedTime !== 'any') {
      const days = parseInt(selectedTime, 10);
      out = out.filter((b) => {
        if (!b.created_at) return true; // can't filter if no timestamp
        const created = new Date(b.created_at).getTime();
        const diffDays = (Date.now() - created) / (1000 * 60 * 60 * 24);
        return diffDays <= days;
      });
    }
    // Apply sorting based on selectedSort
    if (selectedSort === 'newest') {
      out.sort((a, z) => (z.id - a.id));
    } else if (selectedSort === 'oldest') {
      out.sort((a, z) => (a.id - z.id));
    } else if (selectedSort === 'title') {
      out.sort((a, z) => (a.title || '').localeCompare(z.title || ''));
    }
    return out;
  }, [books, query, selectedGrade, selectedSubject, selectedTime, selectedSort]);

  const gradeOptions = useMemo(() => {
    const s = new Set<string>();
    books.forEach((b) => { if (b.grade) s.add(String(b.grade)); });
    return Array.from(s).sort();
  }, [books]);

  const subjectOptions = useMemo(() => {
    const s = new Set<string>();
    books.forEach((b) => { if (b.subject) s.add(String(b.subject)); });
    return Array.from(s).sort();
  }, [books]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageTitle title="Job Board - RAG" />

      {/* Top search and filter bar */}
      <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
        <div className="flex flex-col gap-4">
          {/* Main search and filter controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg w-full bg-gray-50">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-slate-400">
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, author, keywords..."
                className="bg-transparent w-full outline-none text-slate-700"
              />
            </div>
            {/* Placeholder for "Anywhere" dropdown */}
            <select aria-label="Anywhere filter" className="border rounded px-3 py-2 bg-gray-50 text-slate-700">
              <option value="">Anywhere</option>
              {/* Add actual options here if functionality is needed */}
            </select>
            <button className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-gray-50 text-slate-700">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-slate-400">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Filters
            </button>
            <select aria-label="Sort by" value={selectedSort} onChange={(e) => setSelectedSort(e.target.value)} className="border rounded px-3 py-2 bg-gray-50 text-slate-700">
              <option value="newest">Sort by: Newest</option>
              <option value="oldest">Sort by: Oldest</option>
              <option value="title">Sort by: Title</option>
            </select>
          </div>

          {/* Filter dropdowns and active badges */}
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Filter by grade" value={selectedGrade} onChange={(e) => { setSelectedGrade(e.target.value); }} className="border rounded px-2 py-1 text-sm bg-gray-50">
              <option value="">All grades</option>
              {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select aria-label="Filter by subject" value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="border rounded px-2 py-1 text-sm bg-gray-50">
              <option value="">All subjects</option>
              {subjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select aria-label="Filter by time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} className="border rounded px-2 py-1 text-sm bg-gray-50">
              <option value="any">Any time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="365">Last year</option>
            </select>
            <select aria-label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)} className="border rounded px-2 py-1 text-sm bg-gray-50">
              <option value="none">Group: None</option>
              <option value="subject">Group: Subject</option>
              <option value="grade">Group: Grade</option>
            </select>

            {/* Dynamically generated filter badges */}
            {selectedGrade && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Grade: {selectedGrade}
                <button type="button" className="ml-1 h-4 w-4 flex-shrink-0 rounded-full inline-flex items-center justify-center text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none focus:bg-purple-200 focus:text-purple-500" onClick={() => setSelectedGrade('')}>
                  <span className="sr-only">Remove filter</span>
                  <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                    <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6M1 7L7 1" />
                  </svg>
                </button>
              </span>
            )}
            {selectedSubject && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Subject: {selectedSubject}
                <button type="button" className="ml-1 h-4 w-4 flex-shrink-0 rounded-full inline-flex items-center justify-center text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none focus:bg-purple-200 focus:text-purple-500" onClick={() => setSelectedSubject('')}>
                  <span className="sr-only">Remove filter</span>
                  <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                    <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6M1 7L7 1" />
                  </svg>
                </button>
              </span>
            )}
            {selectedTime !== 'any' && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Time: {selectedTime === '7' ? 'Last 7 days' : selectedTime === '30' ? 'Last 30 days' : 'Last year'}
                <button type="button" className="ml-1 h-4 w-4 flex-shrink-0 rounded-full inline-flex items-center justify-center text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none focus:bg-purple-200 focus:text-purple-500" onClick={() => setSelectedTime('any')}>
                  <span className="sr-only">Remove filter</span>
                  <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                    <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6M1 7L7 1" />
                  </svg>
                </button>
              </span>
            )}
            {(selectedGrade || selectedSubject || selectedTime !== 'any') && (
              <button className="text-sm text-slate-500 hover:text-slate-700" onClick={() => { setSelectedGrade(''); setSelectedSubject(''); setSelectedTime('any'); }}>Clear All</button>
            )}
          </div>

          <div className="flex items-center justify-between text-sm text-slate-500">
            <div>We found <span className="font-semibold">{filtered.length}</span> books</div>
          </div>
        </div>
      </div>

      {/* Results list */}
      <div className="mt-6 space-y-4 flex gap-6">
        <div className="flex-1">
          {loading && <div className="text-center text-gray-500 py-8">Loading books...</div>}
          {error && <div className="text-center text-red-600 py-8">{error}</div>}
          {filtered.length === 0 && !loading && !error && <div className="text-center text-gray-500 py-8">No RAG books found.</div>}
          {groupBy === 'none' && filtered.map((b) => (
            <div key={b.id} className={`bg-white m-5 rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md cursor-pointer transition-all duration-200 ease-in-out ${activeBook && activeBook.id === b.id ? 'ring-2 ring-indigo-400' : ''}`} onClick={async () => {
              // set active and load chapters + chunks for sidebar
              setActiveBook(b);
              const key = String(b.id);
              const token = localStorage.getItem('token');
              try {
                const data = await request(`/api/textbook/${b.id}/chapters/`, { headers: { 'Authorization': `Bearer ${token}` } });
                setChaptersByBook((s) => ({ ...s, [key]: data.chapters || [] }));
                // fetch chunks for each chapter
                for (const ch of (data.chapters || [])) {
                  try {
                    const d2 = await request(`/api/textbook/chapter/${ch.id}/chunks/`, { headers: { 'Authorization': `Bearer ${token}` } });
                    setChunksByChapter((s) => ({ ...s, [String(ch.id)]: d2.chunks || [] }));
                  } catch (err) {
                    setChunksByChapter((s) => ({ ...s, [String(ch.id)]: [] }));
                  }
                }
              } catch (err) {
                setChaptersByBook((s) => ({ ...s, [key]: [] }));
              }
            }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-md bg-indigo-100 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="#6B21A8" strokeWidth="1.5"/></svg>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-800">{b.title}</div>
                  <div className="text-sm text-slate-500">{b.author || '—'}</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {b.subject && <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">{b.subject}</span>}
                    {b.grade && <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">Grade {b.grade}</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-sm font-semibold">{b.faiss_index_path ? 'Indexed' : 'Not indexed'}</div>
                  <div className="text-xs text-slate-400">{b.chapters_count} chapters</div>
                </div>
              </div>
            </div>

            {/* inline expansion removed; details shown in sidebar */}
          </div>
          ))}

          {groupBy !== 'none' && (() => {
            const groups: Record<string, any[]> = {};
            for (const b of filtered) {
              const key = groupBy === 'subject' ? (b.subject || 'Other') : (groupBy === 'grade' ? (`Grade ${b.grade || 'Unknown'}`) : 'Other');
              if (!groups[key]) groups[key] = [];
              groups[key].push(b);
            }
            const keys = Object.keys(groups).sort();
            return keys.map((k) => (
              <div key={k} className="mb-6">
                <div className="text-sm font-semibold text-slate-700 mb-2">{k} <span className="text-xs text-slate-400">({groups[k].length})</span></div>
                {groups[k].map((b: any) => (
                  <div key={b.id} className={`bg-white m-2 rounded-xl p-3 shadow-sm border border-gray-200 hover:shadow-md cursor-pointer transition-all duration-200 ease-in-out ${activeBook && activeBook.id === b.id ? 'ring-2 ring-indigo-400' : ''}`} onClick={async () => {
                      setActiveBook(b);
                    const key = String(b.id);
                    const token = localStorage.getItem('token');
                    try {
                      const data = await request(`/api/textbook/${b.id}/chapters/`, { headers: { 'Authorization': `Bearer ${token}` } });
                      setChaptersByBook((s) => ({ ...s, [key]: data.chapters || [] }));
                      for (const ch of (data.chapters || [])) {
                        try {
                          const d2 = await request(`/api/textbook/chapter/${ch.id}/chunks/`, { headers: { 'Authorization': `Bearer ${token}` } });
                          setChunksByChapter((s) => ({ ...s, [String(ch.id)]: d2.chunks || [] }));
                        } catch (err) {
                          setChunksByChapter((s) => ({ ...s, [String(ch.id)]: [] }));
                        }
                      }
                    } catch (err) {
                      setChaptersByBook((s) => ({ ...s, [key]: [] }));
                    }
                  }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-md bg-indigo-100 flex items-center justify-center">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="#6B21A8" strokeWidth="1.2"/></svg>
                        </div>
                        <div>
                          <div className="text-md font-semibold text-slate-800">{b.title}</div>
                          <div className="text-xs text-slate-500">{b.author || '—'}</div>
                        </div>
                      </div>
                      <div className="text-sm text-slate-400">{b.faiss_index_path ? 'Indexed' : 'Not indexed'}</div>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>

        {/* Right side details panel (hidden until a book is active) */}
        {activeBook && (
          <div className="w-96 rag-sidebar">
            <div className="sticky top-0 bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-md bg-indigo-100 flex items-center justify-center text-indigo-700">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5"/></svg>
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-800">{activeBook.title}</div>
                  <div className="text-sm text-slate-600">{activeBook.author || '—'}</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {activeBook.subject && <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">{activeBook.subject}</span>}
                    {activeBook.grade && <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">Grade {activeBook.grade}</span>}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4 text-sm text-slate-700">
                <div className="flex justify-between items-center mb-2">
                  <strong>Index Status:</strong>
                  <span>{activeBook.faiss_index_path ? 'Indexed' : 'Not persisted'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <strong>Chapters Count:</strong>
                  <span>{activeBook.chapters_count}</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="font-bold text-slate-800 mb-3">Chapters</div>
                <ul className="space-y-3 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                  {(chaptersByBook[String(activeBook.id)] || []).map((ch: any) => (
                    <li key={ch.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <button type="button" className="flex justify-between items-center w-full text-left" onClick={() => setOpenChapters((s) => ({ ...s, [String(ch.id)]: !s[String(ch.id)] }))}>
                        <div>
                          <div className="font-semibold text-slate-800">{ch.chapter_number}. {ch.title}</div>
                          <div className="text-xs text-slate-500">Pages: {ch.start_page || '–'} - {ch.end_page || '–'}</div>
                        </div>
                        <span className="text-slate-500">
                          {openChapters[String(ch.id)] ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          )}
                        </span>
                      </button>

                      {openChapters[String(ch.id)] && (chunksByChapter[String(ch.id)] || []).length > 0 && (
                        <ul className="mt-3 space-y-2 pl-4 border-l border-gray-300 ml-2">
                          {(chunksByChapter[String(ch.id)] || []).map((tc: any) => (
                            <li key={tc.id} className="flex items-start justify-between text-sm text-slate-700">
                              <div className="flex-1 pr-2">{tc.text_content.slice(0, 100)}{tc.text_content.length > 100 ? '…' : ''}</div>
                              <button className="flex-shrink-0 text-indigo-600 hover:text-indigo-800 text-xs font-medium" onClick={(e) => { e.stopPropagation(); setModalChunk(tc.text_content); }}>See more</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Vector Search section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="font-bold text-slate-800 mb-3">Vector Search</div>
                <button className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors duration-200" onClick={() => setVectorModalOpen(true)}>Open Vector Search</button>
              </div>
            </div>
          </div>
        )}
        {/* Modal for full chunk text */}
        {modalChunk && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-end">
                <button onClick={() => setModalChunk(null)} className="text-slate-500">Close</button>
              </div>
              <div className="mt-3 text-slate-800 whitespace-pre-wrap">{modalChunk}</div>
            </div>
          </div>
        )}

        {/* Vector search modal */}
        {vectorModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg max-w-xl w-full p-6">
              <div className="flex justify-between items-center">
                <div className="text-lg font-semibold">Vector Search</div>
                <button className="text-slate-500" onClick={() => setVectorModalOpen(false)}>Close</button>
              </div>
              <div className="mt-4">
                <textarea value={vectorQuery} onChange={(e) => setVectorQuery(e.target.value)} rows={4} className="w-full border rounded p-2" placeholder="Ask a question about this book..." />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button className="see-more-btn" onClick={runVectorSearch} disabled={vectorLoading || !vectorQuery.trim()}>{vectorLoading ? 'Searching…' : 'Search'}</button>
                <button className="border rounded px-3 py-2" onClick={() => { setVectorModalOpen(false); setVectorQuery(''); setVectorResults(null); }}>Cancel</button>
              </div>

              {/* Render results inside modal */}
              {vectorResults && (
                <div className="mt-4 space-y-3">
                  <div className="font-medium">Results ({vectorResults.length})</div>
                  {vectorLoading && <div className="text-sm text-slate-500">Searching…</div>}
                  {!vectorLoading && vectorResults.length === 0 && <div className="text-sm text-slate-500">No results</div>}
                  {!vectorLoading && vectorResults.map((r: any, i: number) => (
                    <div key={i} className="chapter-card">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">Chapter: {r.chapter?.chapter_number ?? '–'} — {r.chapter?.title ?? ''}</div>
                          <div className="text-sm mt-1">{r.chunk?.text_content?.slice(0, 240) ?? '—'}</div>
                        </div>
                        <div className="text-xs text-slate-500 text-right ml-4">Score<br />{typeof r.score === 'number' ? r.score.toFixed(4) : '–'}</div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button className="see-more-btn" onClick={() => setModalChunk(r.chunk?.text_content)}>See more</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RagList;
